import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarIcon } from "@heroicons/react/24/outline";
import api from "../../../../utils/axios";
import ProductBreadcrumb from "../components/ProductBreadcrumb";
import ProductSearchBar from "../components/ProductSearchBar";
import ProductStatusBadge from "../components/ProductStatusBadge";
import ProductSummaryCard from "../components/ProductSummaryCard";
import ProductTableAction from "../components/ProductTableAction";

const formatDate = (date) => {
  if (!date) return "-";

  return new Date(date).toLocaleDateString("es-AR");
};

export default function ProductLotsTable() {
  const { tagId, productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [tag, setTag] = useState(null);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchProductDetail = async () => {
      try {
        setIsLoading(true);

        const [productResponse, tagResponse] = await Promise.all([
          api.get(`/productos/${productId}/detail`),
          api.get(`/tags/${tagId}/productsTable`),
        ]);

        setProduct(productResponse.data);
        setTag(tagResponse.data);
      } catch (error) {
        console.log("Error al obtener detalle de producto:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductDetail();
  }, [productId, tagId]);

  const lots = product?.lots || [];

  const filteredLots = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return lots;

    return lots.filter((lot) =>
      `${lot.lotNumber || ""} ${lot.status || ""} ${formatDate(lot.acquireDate)} ${formatDate(lot.expireDate)}`
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [lots, search]);

  return (
    <div className="main-container">
      <div className="tw-flex tw-flex-col tw-gap-4 tw-px-3 tw-py-4">
        <div className="tw-flex tw-items-center tw-gap-3">
          <button
            type="button"
            onClick={() => navigate(`/productos/new-table/tags/${tagId}`)}
            className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 tw-transition hover:tw-bg-slate-50"
          >
            ← Volver
          </button>

          <ProductBreadcrumb
            items={[
              { label: "Etiquetas", to: "/productos/new-table" },
              { label: tag?.name || "Etiqueta", to: `/productos/new-table/tags/${tagId}` },
              { label: product?.name || "Producto" },
            ]}
          />
        </div>

        <ProductSearchBar value={search} onChange={setSearch} />

        {product ? (
          <ProductSummaryCard
            type="product"
            title={product.name}
            status={product.status}
            items={[
              { label: "Código", value: product.code ? `#${product.code}` : "-" },
              { label: "Marca", value: product.brand || "-" },
              { label: "Cantidad total", value: product.quantity ?? 0 },
              { label: "Total de lotes", value: product.totalLots ?? 0 },
            ]}
            onEdit={() => navigate(`/productos/edit/${productId}`)}
          />
        ) : null}

        <div className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white">
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-border-b tw-border-slate-200 tw-bg-white tw-text-left tw-text-xs tw-font-semibold tw-text-slate-900">
                  <th className="tw-w-12 tw-px-5 tw-py-3">
                    <CalendarIcon className="tw-h-4 tw-w-4 tw-text-slate-500" />
                  </th>
                  <th className="tw-px-5 tw-py-3">Número de Lote</th>
                  <th className="tw-px-5 tw-py-3">Estado</th>
                  <th className="tw-px-5 tw-py-3">Fecha de Ingreso</th>
                  <th className="tw-px-5 tw-py-3">Vencimiento</th>
                  <th className="tw-px-5 tw-py-3 tw-text-center">Cantidad</th>
                  <th className="tw-px-5 tw-py-3"></th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="tw-px-5 tw-py-8 tw-text-center tw-text-slate-500">
                      Cargando lotes...
                    </td>
                  </tr>
                ) : filteredLots.length ? (
                  filteredLots.map((lot) => (
                    <tr
                      key={lot._id}
                      className="tw-border-b tw-border-slate-200 tw-transition last:tw-border-b-0 hover:tw-bg-slate-50"
                    >
                      <td className="tw-px-5 tw-py-3">
                        <span className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-cyan-50 tw-text-cyan-700">
                          <CalendarIcon className="tw-h-4 tw-w-4" />
                        </span>
                      </td>
                      <td className="tw-px-5 tw-py-3 tw-font-semibold tw-text-slate-950">
                        #{lot.lotNumber}
                      </td>
                      <td className="tw-px-5 tw-py-3">
                        <ProductStatusBadge status={lot.status} />
                      </td>
                      <td className="tw-px-5 tw-py-3 tw-text-slate-600">
                        {formatDate(lot.acquireDate)}
                      </td>
                      <td className="tw-px-5 tw-py-3 tw-text-slate-600">
                        {formatDate(lot.expireDate)}
                      </td>
                      <td className="tw-px-5 tw-py-3 tw-text-center tw-font-semibold">
                        {lot.quantity ?? 0}
                      </td>
                      <td className="tw-px-5 tw-py-3">
                        <ProductTableAction
                          onEdit={() => navigate(`/lotes/edit/${lot._id}`)}
                          showOpen={false}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="tw-px-5 tw-py-8 tw-text-center tw-text-slate-500">
                      No se encontraron lotes
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-border-t tw-border-slate-200 tw-bg-white tw-px-5 tw-py-4 tw-text-sm tw-font-semibold tw-text-cyan-700 tw-transition hover:tw-bg-cyan-50"
          >
            + Agregar nuevo lote
          </button>
        </div>
      </div>
    </div>
  );
}